var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { capitalize } from 'lodash';
import { CustomFormElement } from 'components';
import { ChecklistRequirementsNames } from 'interface';
import { createApplication, deleteApplicationAttachment, downloadApplicationAttachment, downloadChecklistAttachment, fetchSalesRepresentatives, previewApplicationAttachment, previewChecklistAttachment, updateApplication, uploadApplicationAttachment } from 'services';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormLabel } from 'components/common/Form';
import { AptiveIcon, Button, Checkbox } from '@aptive-env/storybook';
import LicenseApplicationFooter from './LicenseApplicationFooter';
import LicenseApplicationHeader from './LicenseApplicationHeader';
import FilePreview from 'components/common/Form/CustomFile/FilePreview';
import * as yup from 'yup';
var applicationFormSchema = yup.object().shape({
    primary_sales_rep_id: yup.number().required('Primary applicant is required'),
    additional_sales_rep_ids: yup.array().of(yup.number().required())
        .required()
        .min(1, 'Additional applicants are required')
        .test('not-in-primary', 'Primary applicant cannot be in additional applicants', function (value) {
        var primary_sales_rep_id = this.parent.primary_sales_rep_id;
        return !value || !value.includes(primary_sales_rep_id);
    }),
    requirements: yup.object().shape({
        registration_in_person: yup.boolean().optional(),
        wet_signature: yup.boolean().optional(),
        digital_signature: yup.boolean().optional(),
        background_check: yup.boolean().optional(),
        fingerprints: yup.boolean().optional(),
        identification_requirements: yup.boolean().optional(),
        photo_requirements: yup.boolean().optional(),
        bond: yup.boolean().optional(),
        certificate_of_insurance: yup.boolean().optional(),
        articles_of_incorporation: yup.boolean().optional(),
        business_license: yup.boolean().optional(),
        certificate_of_authority: yup.boolean().optional(),
        letter_of_authorization: yup.boolean().optional(),
        personal_references: yup.boolean().optional(),
        local_references: yup.boolean().optional(),
        professional_references: yup.boolean().optional(),
        sales_tax_id: yup.boolean().optional(),
        vehicle_registration: yup.boolean().optional(),
        vehicle_insurance: yup.boolean().optional(),
        additional_appointments_required: yup.boolean().optional(),
    }).required(),
    notes: yup.string().optional(),
    attachments: yup.array().of(yup.mixed().required()).optional(),
});
var LicenseApplicationForm = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var details = _a.details, attachments = _a.attachments, checklistAttachments = _a.checklistAttachments, onCancel = _a.onCancel, onSubmit = _a.onSubmit;
    var methods = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        defaultValues: {
            primary_sales_rep_id: (_b = details.primary_sales_rep) === null || _b === void 0 ? void 0 : _b.id,
            additional_sales_rep_ids: details.additional_sales_reps ? details.additional_sales_reps.map(function (item) { return item.id; }) : [],
            notes: (_c = details.notes) !== null && _c !== void 0 ? _c : undefined,
            attachments: attachments,
            requirements: Object.keys((_d = details.requirements) !== null && _d !== void 0 ? _d : {})
                .filter(function (key) {
                var _a, _b;
                return details.id
                    ? typeof ((_a = details.requirements) === null || _a === void 0 ? void 0 : _a[key]) === 'boolean'
                    : ((_b = details.requirements) === null || _b === void 0 ? void 0 : _b[key]) === true;
            })
                .reduce(function (a, v) {
                var _a;
                var key = v;
                a[key] = details.id ? (_a = details.requirements) === null || _a === void 0 ? void 0 : _a[key] : false;
                return a;
            }, {}),
        },
        resolver: yupResolver(applicationFormSchema),
    });
    var _h = methods.formState, errors = _h.errors, isValid = _h.isValid, isSubmitting = _h.isSubmitting, register = methods.register, setValue = methods.setValue, getValues = methods.getValues, handleSubmit = methods.handleSubmit;
    var _j = useState(true), isRequirementsSectionExpanded = _j[0], setIsRequirementsSectionExpanded = _j[1];
    var _k = useState([]), salesRepresentatives = _k[0], setSalesRepresentatives = _k[1];
    var loadSalesRepresentatives = function () { return __awaiter(void 0, void 0, void 0, function () {
        var fetchSalesRepresentativesResponse;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, fetchSalesRepresentatives({
                        page: 1,
                        per_page: 1000000
                    })];
                case 1:
                    fetchSalesRepresentativesResponse = _a.sent();
                    setSalesRepresentatives(fetchSalesRepresentativesResponse.result.reps);
                    return [2 /*return*/];
            }
        });
    }); };
    var handleCheckRequirement = function (event, key) {
        var _a;
        event.preventDefault();
        event.stopPropagation();
        var requirements = getValues('requirements');
        var newRequirements = __assign(__assign({}, requirements), (_a = {}, _a[key] = !requirements[key], _a));
        setValue('requirements', newRequirements, { shouldValidate: true, shouldDirty: true });
    };
    var handleOpenApplicationUrl = function () {
        if (details.submission_url) {
            window.open(details.submission_url, '_blank');
        }
    };
    var handlePreviewFile = function (file) { return __awaiter(void 0, void 0, void 0, function () {
        var response, url, url;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(file.id && details.id)) return [3 /*break*/, 2];
                    return [4 /*yield*/, previewApplicationAttachment(details.id, file.id)];
                case 1:
                    response = _a.sent();
                    url = URL.createObjectURL(response);
                    window.open(url, '_blank');
                    URL.revokeObjectURL(url);
                    return [3 /*break*/, 3];
                case 2:
                    url = URL.createObjectURL(file);
                    window.open(url, '_blank');
                    URL.revokeObjectURL(url);
                    _a.label = 3;
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var handleDownloadFile = function (file) { return __awaiter(void 0, void 0, void 0, function () {
        var attachment, response, url, link, asFile, url, link;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(file.id && details.id)) return [3 /*break*/, 2];
                    attachment = file;
                    return [4 /*yield*/, downloadApplicationAttachment(details.id, attachment.id)];
                case 1:
                    response = _a.sent();
                    url = URL.createObjectURL(response);
                    link = document.createElement('a');
                    link.href = url;
                    link.download = attachment.filename;
                    link.click();
                    URL.revokeObjectURL(url);
                    return [3 /*break*/, 3];
                case 2:
                    asFile = file;
                    url = URL.createObjectURL(asFile);
                    link = document.createElement('a');
                    link.href = url;
                    link.download = asFile.name;
                    link.click();
                    URL.revokeObjectURL(url);
                    _a.label = 3;
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var handlePreviewChecklistFile = function (file) { return __awaiter(void 0, void 0, void 0, function () {
        var response, url, url;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(file.id && details.checklist_id)) return [3 /*break*/, 2];
                    return [4 /*yield*/, previewChecklistAttachment(details.checklist_id, file.id)];
                case 1:
                    response = _a.sent();
                    url = URL.createObjectURL(response);
                    window.open(url, '_blank');
                    URL.revokeObjectURL(url);
                    return [3 /*break*/, 3];
                case 2:
                    url = URL.createObjectURL(file);
                    window.open(url, '_blank');
                    URL.revokeObjectURL(url);
                    _a.label = 3;
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var handleDownloadChecklistFile = function (file) { return __awaiter(void 0, void 0, void 0, function () {
        var attachment, response, url, link, asFile, url, link;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(file.id && details.checklist_id)) return [3 /*break*/, 2];
                    attachment = file;
                    return [4 /*yield*/, downloadChecklistAttachment(details.checklist_id, attachment.id)];
                case 1:
                    response = _a.sent();
                    url = URL.createObjectURL(response);
                    link = document.createElement('a');
                    link.href = url;
                    link.download = attachment.filename;
                    link.click();
                    URL.revokeObjectURL(url);
                    return [3 /*break*/, 3];
                case 2:
                    asFile = file;
                    url = URL.createObjectURL(asFile);
                    link = document.createElement('a');
                    link.href = url;
                    link.download = asFile.name;
                    link.click();
                    URL.revokeObjectURL(url);
                    _a.label = 3;
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var handleChange = useCallback(function (event) {
        var _a = event.target, name = _a.name, value = _a.value;
        setValue(name, value, { shouldValidate: true, shouldDirty: true });
    }, [setValue]);
    var handleSubmitForm = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var values, applicationId_1, response, currentAttachmentIds_1, deletedAttachmentsIds, _i, _a, attachment, response, _b, _c, attachment, e_1;
        var _d, _e;
        return __generator(this, function (_f) {
            switch (_f.label) {
                case 0:
                    _f.trys.push([0, 13, , 14]);
                    values = getValues();
                    applicationId_1 = details.id;
                    if (!applicationId_1) return [3 /*break*/, 6];
                    return [4 /*yield*/, updateApplication(applicationId_1, __assign({ primary_sales_rep_id: values.primary_sales_rep_id, additional_sales_rep_ids: values.additional_sales_rep_ids, notes: values.notes }, values.requirements))];
                case 1:
                    response = _f.sent();
                    if (!(response._metadata.success && values.attachments && values.attachments.length > 0)) return [3 /*break*/, 5];
                    currentAttachmentIds_1 = values.attachments.map(function (attachment) { return attachment.id; }).filter(function (id) { return id; });
                    deletedAttachmentsIds = attachments
                        .map(function (attachment) { return attachment.id; })
                        .filter(function (attachmentId) { return !currentAttachmentIds_1.includes(attachmentId); });
                    deletedAttachmentsIds.forEach(function (attachmentId) { return __awaiter(void 0, void 0, void 0, function () {
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0: return [4 /*yield*/, deleteApplicationAttachment(applicationId_1, attachmentId)];
                                case 1:
                                    _a.sent();
                                    return [2 /*return*/];
                            }
                        });
                    }); });
                    _i = 0, _a = values.attachments;
                    _f.label = 2;
                case 2:
                    if (!(_i < _a.length)) return [3 /*break*/, 5];
                    attachment = _a[_i];
                    if (!!attachment.id) return [3 /*break*/, 4];
                    return [4 /*yield*/, uploadApplicationAttachment(applicationId_1, attachment)];
                case 3:
                    _f.sent();
                    _f.label = 4;
                case 4:
                    _i++;
                    return [3 /*break*/, 2];
                case 5:
                    onSubmit({
                        success: (_d = response._metadata.success) !== null && _d !== void 0 ? _d : false,
                        message: response.result.message
                    });
                    return [3 /*break*/, 12];
                case 6:
                    if (!details.checklist_id) return [3 /*break*/, 12];
                    return [4 /*yield*/, createApplication(__assign({ checklist_id: details.checklist_id, primary_sales_rep_id: values.primary_sales_rep_id, additional_sales_rep_ids: values.additional_sales_rep_ids, notes: values.notes }, values.requirements))];
                case 7:
                    response = _f.sent();
                    if (!(response.result.id && values.attachments && values.attachments.length > 0)) return [3 /*break*/, 11];
                    _b = 0, _c = values.attachments;
                    _f.label = 8;
                case 8:
                    if (!(_b < _c.length)) return [3 /*break*/, 11];
                    attachment = _c[_b];
                    return [4 /*yield*/, uploadApplicationAttachment(response.result.id, attachment)];
                case 9:
                    _f.sent();
                    _f.label = 10;
                case 10:
                    _b++;
                    return [3 /*break*/, 8];
                case 11:
                    onSubmit({
                        success: (_e = response._metadata.success) !== null && _e !== void 0 ? _e : false,
                        message: response.result.message
                    });
                    _f.label = 12;
                case 12: return [3 /*break*/, 14];
                case 13:
                    e_1 = _f.sent();
                    console.error(e_1);
                    onSubmit({
                        success: false,
                        message: "Error in ".concat(details.id ? 'updating' : 'creating', " application.")
                    });
                    return [3 /*break*/, 14];
                case 14: return [2 /*return*/];
            }
        });
    }); }, [attachments, details, getValues, onSubmit]);
    useEffect(function () {
        loadSalesRepresentatives();
    }, []);
    return (_jsx("div", __assign({ className: "w-[576px]" }, { children: _jsx(FormProvider, __assign({}, methods, { children: _jsxs("form", __assign({ noValidate: true, onSubmit: handleSubmit(handleSubmitForm) }, { children: [_jsx(LicenseApplicationHeader, { title: details.id ? 'Edit Application' : 'New Application', onClose: onCancel }), _jsxs("div", __assign({ className: "px-6 grid grid-cols-1 py-6 gap-y-6 gap-x-4 sm:grid-cols-6 max-h-[473px] overflow-y-auto no-scrollbar" }, { children: [_jsxs("div", __assign({ className: "sm:col-span-6" }, { children: [_jsx(FormLabel, { label: "Location Name", required: false, htmlFor: "location" }), _jsx("div", __assign({ className: "capitalize" }, { children: [(_e = details.state) === null || _e === void 0 ? void 0 : _e.name, (_f = details.county) === null || _f === void 0 ? void 0 : _f.name, (_g = details.municipality) === null || _g === void 0 ? void 0 : _g.name].filter(function (item) { return item; }).join(' - ') }))] })), details.submission_type !== 'online' && (_jsxs(_Fragment, { children: [_jsx(CustomFormElement, { id: "primary_sales_rep_id", name: "primary_sales_rep_id", label: "Primary applicant", type: "multiSelect", placeholder: "", isMulti: false, options: salesRepresentatives.map(function (salesRepresentative) { return ({
                                            value: salesRepresentative.id,
                                            label: salesRepresentative.name,
                                        }); }), onChange: handleChange, value: getValues('primary_sales_rep_id'), register: register, error: errors.primary_sales_rep_id, formElementWrapperClassName: 'sm:col-span-6', required: true }), _jsx(CustomFormElement, { id: "additional_sales_rep_ids", name: "additional_sales_rep_ids", label: "Additional applicants (you can select more than one)", type: "multiSelect", placeholder: "", isMulti: true, options: salesRepresentatives.map(function (salesRepresentative) { return ({
                                            value: salesRepresentative.id,
                                            label: salesRepresentative.name,
                                        }); }), onChange: handleChange, value: getValues('additional_sales_rep_ids'), register: register, error: errors.additional_sales_rep_ids, formElementWrapperClassName: 'sm:col-span-6', required: true })] })), _jsxs("div", __assign({ className: "sm:col-span-6" }, { children: [_jsx(FormLabel, { label: "Priotity", required: false, htmlFor: "priotity" }), _jsxs("div", __assign({ className: "flex items-center flex-1 py-4 text-sm text-[#000000]" }, { children: [details.priority === 'high' && (_jsx(AptiveIcon, { className: "w-[16px] h-[16px] stroke-[#F05252] fill-none", icon: "chevronUp" })), details.priority === 'medium' && (_jsx(AptiveIcon, { className: "w-[16px] h-[16px] stroke-[#F59E0B] fill-none", icon: "menuAlt4" })), details.priority === 'low' && (_jsx(AptiveIcon, { className: "w-[16px] h-[16px] stroke-[#0EA5E9] fill-none", icon: "chevronDown" })), _jsx("span", __assign({ className: "pl-3" }, { children: capitalize(details.priority) }))] }))] })), details.submission_type === 'online' && (_jsxs(_Fragment, { children: [_jsx("div", __assign({ className: "sm:col-span-6" }, { children: _jsx("div", __assign({ className: "text-[#4B5563]" }, { children: "This application is an online application. Please click the button below to open the application portal" })) })), _jsxs("div", __assign({ className: "sm:col-span-6 flex gap-2" }, { children: [_jsx(CustomFormElement, { id: "submission_url", name: "submission_url", label: "Submission URL", placeholder: "http://www.example.com/applications", type: "text", hasCopyButton: true, formElementWrapperClassName: "w-full", value: details.submission_url, onChange: function () { } }), _jsx(Button, __assign({ size: "default", variant: "default", className: "mt-[22px] h-[38px] flex-none w-[200px]", onClick: handleOpenApplicationUrl }, { children: "Open Application URL" }))] }))] })), details.submission_type !== 'online' && (_jsxs(_Fragment, { children: [_jsxs("div", __assign({ className: "sm:col-span-6" }, { children: [_jsxs("div", __assign({ className: 'flex justify-between' }, { children: [_jsx("div", __assign({ className: 'text-sm font-medium text-gray-700' }, { children: "Requirements" })), _jsx("button", __assign({ className: 'flex gap-5 mr-3 cursor-pointer', onClick: function (e) {
                                                            e.preventDefault();
                                                            setIsRequirementsSectionExpanded(!isRequirementsSectionExpanded);
                                                        } }, { children: _jsx(AptiveIcon, { className: "w-5 h-5 stroke-[#4B5563] fill-none", icon: isRequirementsSectionExpanded ? 'chevronUp' : 'chevronDown' }) }))] })), isRequirementsSectionExpanded && (_jsx("div", __assign({ className: "mt-5" }, { children: Object.keys(getValues('requirements')).map(function (key, index) { return (_jsx("div", __assign({ className: index % 2 === 0 ? 'sm:col-span-3' : 'sm:col-span-4' }, { children: _jsx(Checkbox, __assign({ className: "rounded-sm border-gray-400", checked: getValues('requirements')[key], onClick: function (event) { return handleCheckRequirement(event, key); } }, { children: ChecklistRequirementsNames[key] })) }), key)); }) })))] })), _jsx("div", __assign({ className: "sm:col-span-6" }, { children: _jsx(FilePreview, { files: checklistAttachments, onPreview: handlePreviewChecklistFile, onDownload: handleDownloadChecklistFile }) })), _jsx(CustomFormElement, { id: "notes", name: "notes", label: "Notes", placeholder: "Something about myself", rows: 1, type: "textArea", onChange: handleChange, value: getValues('notes'), register: register, error: errors.notes, formElementWrapperClassName: 'sm:col-span-6' }), _jsx(CustomFormElement, { id: "attachments", name: "attachments", type: "file", attachmentLabel: "Attachments", onChange: handleChange, defaultFiles: getValues('attachments'), acceptedFileTypes: ['application/pdf'], maxFileSize: 30 * 1024 * 1024, maxFileCount: 1, register: register, error: errors.attachments, formElementWrapperClassName: 'sm:col-span-6', onPreviewFile: handlePreviewFile, onDownloadFile: handleDownloadFile })] }))] })), details.submission_type !== 'online' && (_jsx(LicenseApplicationFooter, { isCancelDisabled: false, isSaveDisabled: !isValid || isSubmitting, onCancel: onCancel, onSave: function () { } }))] })) })) })));
};
export default LicenseApplicationForm;
